import React from 'react';
import { recordError } from '../lib/rum';

interface ErrorBoundaryProps {
    /** Child elements to render normally when no error occurs. */
    children: React.ReactNode;
    /** Optional custom fallback UI to show when an error is caught. */
    fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

/**
 * App-level error boundary that catches unhandled render errors and displays
 * a fallback UI instead of a white screen. Provides "Try Again" and "Go Home"
 * recovery options.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo): void {
        // eslint-disable-next-line no-console -- last-resort diagnostic for an unrecoverable render error; logs the React Error and componentStack as-is (message/stack are arbitrary and unscrubbed here), but the paired recordError() below scrubs message/stack via scrubValuePatterns, is rate-limited, and no-ops when RUM is unconfigured
        console.error('ErrorBoundary caught an error:', error, info);
        // Best-effort telemetry — recordError is fully guarded and never throws.
        recordError(error, { source: 'ErrorBoundary', componentStack: info.componentStack });
    }

    handleReset = (): void => {
        this.setState({ hasError: false, error: null });
    };

    render(): React.ReactNode {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100vh',
                        padding: '2rem',
                        textAlign: 'center',
                        backgroundColor: 'var(--color-bg, #ffffff)',
                        color: 'var(--color-text, #1a1a1a)',
                    }}
                >
                    <h1 style={{ fontSize: '1.75rem', marginBottom: '0.75rem' }}>Something went wrong</h1>
                    <p style={{ marginBottom: '1.5rem', color: 'var(--color-text-muted, #6b7280)' }}>
                        An unexpected error occurred. You can try again or return to the home page.
                    </p>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                        <button
                            onClick={this.handleReset}
                            style={{
                                padding: '0.5rem 1.25rem',
                                borderRadius: '0.375rem',
                                border: '1px solid var(--color-border, #d1d5db)',
                                backgroundColor: 'var(--color-primary, #3b82f6)',
                                color: '#ffffff',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                            }}
                        >
                            Try Again
                        </button>
                        <a
                            href="/"
                            style={{
                                padding: '0.5rem 1.25rem',
                                borderRadius: '0.375rem',
                                border: '1px solid var(--color-border, #d1d5db)',
                                backgroundColor: 'transparent',
                                color: 'var(--color-text, #1a1a1a)',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                            }}
                        >
                            Go Home
                        </a>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

interface RouteErrorBoundaryState {
    hasError: boolean;
}

/**
 * Route-level error boundary — a lighter version that shows a page-scoped error
 * message with navigation options. Use this to wrap individual route components
 * so one broken route does not take down the entire app.
 */
class RouteErrorBoundary extends React.Component<ErrorBoundaryProps, RouteErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): RouteErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo): void {
        // Render errors inside a route would otherwise produce the fallback UI
        // with zero diagnostic trail. Log via console.error so the failure is
        // visible in the browser console and any captured-console telemetry.
        // eslint-disable-next-line no-console -- last-resort diagnostic for a render error inside a route; logs the React Error and componentStack as-is (message/stack are arbitrary and unscrubbed here), but the paired recordError() below scrubs message/stack via scrubValuePatterns, is rate-limited, and no-ops when RUM is unconfigured
        console.error('RouteErrorBoundary caught:', error, info);
        // Best-effort telemetry — recordError is fully guarded and never throws.
        recordError(error, { source: 'RouteErrorBoundary', componentStack: info.componentStack });
    }

    render(): React.ReactNode {
        if (this.state.hasError) {
            return (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '4rem 2rem',
                        textAlign: 'center',
                        color: 'var(--color-text, #1a1a1a)',
                    }}
                >
                    <h2 style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>This page encountered an error</h2>
                    <p style={{ marginBottom: '1.5rem', color: 'var(--color-text-muted, #6b7280)' }}>Try navigating to another page or return home.</p>
                    <a
                        href="/"
                        style={{
                            padding: '0.5rem 1.25rem',
                            borderRadius: '0.375rem',
                            border: '1px solid var(--color-border, #d1d5db)',
                            color: 'var(--color-text, #1a1a1a)',
                            textDecoration: 'none',
                            fontSize: '0.875rem',
                        }}
                    >
                        Go Home
                    </a>
                </div>
            );
        }

        return this.props.children;
    }
}

export { RouteErrorBoundary };
export default ErrorBoundary;
